// src/components/questionanswer/QnASpring.jsx
import React, { useEffect, useMemo, useState } from "react";
import basicQuestions from "./qnaJSONdata/QnASpring.json";
import scenarioQuestions from "./qnaJSONdata/QnAScenarioSpring.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnASpring + Download PDF
 * - LEFT: sticky "On this page" + sponsored ad
 * - MAIN: inline questions with filter; header includes Download PDF + Print
 * - RIGHT: upgraded sidebar (already has ad card)
 */

// ============== AdSense helpers (same pattern as Java) ==============
const ADSENSE_CLIENT = "ca-pub-9033879555371063"; // <-- your client id

function useAdSense(clientId) {
  useEffect(() => {
    if (!clientId) return;
    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
    }
  }, [clientId]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
}) {
  useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);
  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

// ============== Page bits ==============
const normalize = (s = "") => s.toLowerCase();

// You can override via .env: REACT_APP_SPRING_PDF_URL=https://cdn.yoursite.com/spring.pdf
const SPRING_PDF_URL =
  process.env.REACT_APP_SPRING_PDF_URL ||
  `${process.env.PUBLIC_URL || ""}/pdfs/spring-interview-questions.pdf`;

function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 380 }}
        type="search"
        placeholder="Filter questions (e.g., IoC, DI, @Transactional, AOP, Boot auto-config)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-3">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft() {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small">
          <a className="nav-link px-0" href="#basics">Spring Basics</a>
          <a className="nav-link px-0" href="#scenarios">Scenario-Based Q&amp;A</a>
          <a
            className="nav-link px-0 mt-2 text-decoration-underline"
            href={SPRING_PDF_URL}
            download="TechVenture-Spring-Interview-Questions.pdf"
            aria-label="Download Spring Q&A as PDF"
          >
            ⬇︎ Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function LeftSponsoredCard() {
  return (
    <div className="card shadow-sm border-0 mt-3">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">Sponsored</h2>
        <AdSlot adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

// ============== Main component ==============
export default function QnASpring() {
  useAdSense(ADSENSE_CLIENT);

  const [query, setQuery] = useState("");

  const totalCount = (basicQuestions?.length || 0) + (scenarioQuestions?.length || 0);

  const filteredBasics = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return basicQuestions;
    return basicQuestions.filter((x) => normalize(x.question + " " + x.answer).includes(qn));
  }, [query]);

  const filteredScenarios = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return scenarioQuestions;
    return scenarioQuestions.filter((x) => normalize(x.question + " " + x.answer).includes(qn));
  }, [query]);

  const shownCount = (filteredBasics?.length || 0) + (filteredScenarios?.length || 0);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page + Sponsored ad (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft />
            <LeftSponsoredCard />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h1 className="h2 mb-1">Spring Framework Interview Questions</h1>
                <p className="text-muted mb-0">
                  Curated questions for Spring Core, Boot, Data, AOP, and real-world scenarios. Use the filter to jump to what you need.
                </p>
              </div>
              <div className="btn-group">
                <a
                  href={SPRING_PDF_URL}
                  className="btn btn-outline-danger btn-sm"
                  download="TechVenture-Spring-Interview-Questions.pdf"
                  aria-label="Download Spring Interview Questions PDF"
                >
                  Download PDF
                </a>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => window.print()}
                  aria-label="Print this page"
                >
                  Print
                </button>
              </div>
            </div>
          </header>

          <FilterBar value={query} onChange={setQuery} total={totalCount} shown={shownCount} />

          {/* Basics */}
          <section id="basics" className="pt-2">
            <h2 className="h4 mt-3">Spring Basics</h2>
            <p className="text-body-secondary">
              Dependency Injection/IoC, bean scopes/lifecycle, configuration styles (Java/XML/annotations),
              Boot auto-configuration, profiles, properties, and more.
            </p>
            <QAInlineList items={filteredBasics} />
          </section>

          {/* In-article ad (centered) */}
          <div className="my-3 text-center">
            <AdSlot adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
          </div>

          {/* Scenarios */}
          <section id="scenarios" className="pt-2">
            <h2 className="h4 mt-4">Scenario-Based Q&amp;A</h2>
            <p className="text-body-secondary">
              Practical situations covering transactions, @Transactional pitfalls, AOP, caching, REST/web,
              data access, testing, and troubleshooting.
            </p>
            <QAInlineList items={filteredScenarios} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: When answering Spring questions, describe bean wiring, configuration, and runtime behavior
            (context, proxies, transaction boundaries) with concrete examples.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar (already has "Sponsored" card + AdSlot) */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
